/**
 * The clearing (decision 0015), Daniel's idea of 2026-10-17. The clearing around the cabin,
 * ringed by mist, where the player sets out what they have found on their expeditions and only
 * that (Daniel, 2026-10-17). The ground is meadow, forest, water or mountain and dresses itself
 * by the world's own rules; the things on it are the player's. Saved in this browser. In the
 * game for every player since 2026-10-19; docs/research/monetization.md, section 10, places it
 * on the free and paid line.
 */

import { CATALOG, type Category, type FamilyDef, familyOf as familyDef } from "@mistsweeper/assets";
import { type Coord, Game, mix32, type RunRecord, Terrain } from "@mistsweeper/core";
import {
  bearsObjects,
  canGraze,
  creatureOn,
  deerAt,
  HOME,
  objectsOn,
  type Pick,
  type Placement,
  SWAY,
  type Sway,
  WATER_DROP,
  type WorldSource,
} from "@mistsweeper/renderer";
import { getJson, setJson } from "./storage.ts";

/** Tiles from the cabin to the clearing's edge: a small clearing is 9 tiles square, a large one 15. */
export const REACH_SMALL = 4;
export const REACH_LARGE = 7;
const SIDE = 2 * REACH_LARGE + 1;
const SALT_TURN = 0x7475726e; // "turn"
const SALT_GRAZE = 0x6772617a; // "graz"
const SALT_NUDGE = 0x6e756467; // "nudg"

/**
 * A trial of how things set out turn (Daniel, 2026-10-19), from the address: `&turn=nudge` stands
 * each thing a few degrees off its step, as houses stand in an old village. Null, the default,
 * keeps to the steps.
 */
export type TurnTrial = "nudge" | null;
let turnTrial: TurnTrial = null;

export function setTurnTrial(trial: TurnTrial): void {
  turnTrial = trial;
}

/** How far the nudge trial stands a thing off its step, at most, in radians: seven degrees. */
const NUDGE = (7 * Math.PI) / 180;

/** The nudge of the thing set out on a tile: the same every time, within NUDGE either way. */
function nudgeAt(x: number, y: number): number {
  if (turnTrial !== "nudge") return 0;
  return ((mix32(x, y, SALT_NUDGE) / 4294967296) * 2 - 1) * NUDGE;
}

export type Ground = "meadow" | "forest" | "water" | "mountain";
export const GROUNDS: readonly Ground[] = ["meadow", "forest", "water", "mountain"];
const CODE: Readonly<Record<Ground, string>> = {
  meadow: "g",
  forest: "f",
  water: "w",
  mountain: "m",
};
const FROM_CODE: Readonly<Record<string, Ground>> = {
  g: "meadow",
  f: "forest",
  w: "water",
  m: "mountain",
};
const TERRAIN: Readonly<Record<Ground, Terrain>> = {
  meadow: Terrain.grass,
  forest: Terrain.forest,
  water: Terrain.water,
  mountain: Terrain.mountain,
};

export type { Category } from "@mistsweeper/assets";
export const CATEGORIES: readonly Category[] = ["plants", "stones", "houses", "creatures"];

const SWAYS: Readonly<Record<FamilyDef["sway"], Sway>> = {
  none: SWAY.none,
  tree: SWAY.tree,
  grass: SWAY.grass,
  crop: SWAY.crop,
};

/**
 * The family of a model that can be set out, by the catalog (decision 0019): `iso-spruce-6` is
 * a spruce, `iso-house-a1` a house. Null for parts, the mist's silhouettes and the wild animals.
 */
function settable(name: string): FamilyDef | null {
  const family = familyDef(name);
  return family?.category ? family : null;
}

/** The name of the family of a model that can be set out; null for parts and the animals. */
export function familyOf(name: string): string | null {
  return settable(name)?.name ?? null;
}

/** The kind of a model that can be set out, as the tray sorts them; null for the rest. */
export function categoryOf(name: string): Category | null {
  return settable(name)?.category ?? null;
}

/** The world's terrain of a ground of the clearing. */
export function terrainOf(ground: Ground): Terrain {
  return TERRAIN[ground];
}

export interface Item {
  readonly name: string;
  readonly x: number;
  readonly y: number;
  /** Eighths of a full turn, 0 up to 8, in halves: sixteenths. */
  readonly turn: number;
}

export interface ClearingState {
  readonly v: 1;
  /** Tiles from the cabin to the edge: REACH_SMALL or REACH_LARGE. */
  readonly reach: number;
  /**
   * One letter per tile of the large clearing, row by row from (-7, -7): g meadow, f forest,
   * w water, m mountain. Ground beyond a small clearing's edge waits under the mist.
   */
  readonly ground: string;
  readonly items: readonly Item[];
}

function cell(x: number, y: number): number | null {
  if (Math.abs(x) > REACH_LARGE || Math.abs(y) > REACH_LARGE) return null;
  return (y + REACH_LARGE) * SIDE + (x + REACH_LARGE);
}

/**
 * The first clearing: open meadow round the cabin and toward the camera, a pond to the front left,
 * forest behind and at the sides, and rock in the far corner, so what is set out stays in view.
 */
export function defaultClearing(): ClearingState {
  const ground: string[] = [];
  for (let y = -REACH_LARGE; y <= REACH_LARGE; y += 1) {
    for (let x = -REACH_LARGE; x <= REACH_LARGE; x += 1) {
      const d = Math.max(Math.abs(x), Math.abs(y));
      let g: Ground = "meadow";
      if ((d >= 4 && x + y < 3) || (d >= 6 && x + y < 8)) g = "forest";
      if (x >= 5 && y <= -3) g = "mountain";
      if ((x === -2 || x === -1) && (y === 2 || y === 3)) g = "water";
      ground.push(CODE[g]);
    }
  }
  return { v: 1, reach: REACH_SMALL, ground: ground.join(""), items: [cabin()] };
}

function cabin(): Item {
  return { name: HOME, x: 0, y: 0, turn: 0 };
}

let first: ClearingState | null = null;

/** The ground a tile has in the first clearing, which clearing the tile gives back. */
export function startGroundAt(x: number, y: number): Ground {
  first ??= defaultClearing();
  return groundAt(first, x, y);
}

export function inside(state: ClearingState, x: number, y: number): boolean {
  return Math.abs(x) <= state.reach && Math.abs(y) <= state.reach;
}

export function groundAt(state: ClearingState, x: number, y: number): Ground {
  const i = cell(x, y);
  if (i === null) return "forest";
  return FROM_CODE[state.ground[i] ?? "g"] ?? "meadow";
}

/** The tiles a model stands on along x and along y: 2 for a massif, else 1. */
export function sizeOf(name: string): number {
  return settable(name)?.size ?? 1;
}

/**
 * Whether a model may stand in the cabin's place as the player's own cabin (decision 0029): a
 * house of one tile to live in, as its family's catalog entry has it.
 */
export function canBeHome(name: string): boolean {
  return familyDef(name)?.home === true && sizeOf(name) === 1;
}

/** The houses found that can be the player's cabin, in the tray's order; the first cabin aside. */
export function homeChoices(entries: readonly CatalogEntry[]): CatalogEntry[] {
  return entries.filter((e) => e.found && canBeHome(e.name));
}

/** The tiles an item stands on: its own, and for a massif the three beyond it in +x and +y. */
export function tilesOf(item: {
  readonly name: string;
  readonly x: number;
  readonly y: number;
}): Coord[] {
  const n = sizeOf(item.name);
  const out: Coord[] = [];
  for (let dy = 0; dy < n; dy += 1) {
    for (let dx = 0; dx < n; dx += 1) out.push({ x: item.x + dx, y: item.y + dy });
  }
  return out;
}

function covers(item: Item, x: number, y: number): boolean {
  const n = sizeOf(item.name);
  return x >= item.x && x < item.x + n && y >= item.y && y < item.y + n;
}

/** What stands on a tile: set out there, or a massif set out on the block the tile belongs to. */
export function itemAt(state: ClearingState, x: number, y: number): Item | null {
  return state.items.find((it) => covers(it, x, y)) ?? null;
}

/** Whether a model may stand on the ground: boats and jetties need water, houses land. */
export function fits(name: string, ground: Ground): boolean {
  const where = settable(name)?.where ?? "land";
  if (where === "water") return ground === "water";
  if (where === "land") return ground !== "water";
  return true;
}

export type Refusal = "mist" | "cabin" | "needsWater" | "needsLand";
/** An edit's outcome: the clearing after it, the same object when nothing changed, or why not. */
export type Edit = { readonly state: ClearingState } | { readonly refused: Refusal };

function withItems(state: ClearingState, items: readonly Item[]): ClearingState {
  return { ...state, items };
}

/** Paints a tile's ground; a thing that no longer fits there is taken away. */
export function paint(state: ClearingState, x: number, y: number, ground: Ground): Edit {
  if (!inside(state, x, y)) return { refused: "mist" };
  if (x === 0 && y === 0) return { refused: "cabin" };
  const i = cell(x, y);
  if (i === null || groundAt(state, x, y) === ground) return { state };
  const next = `${state.ground.slice(0, i)}${CODE[ground]}${state.ground.slice(i + 1)}`;
  const item = itemAt(state, x, y);
  const items =
    item && !fits(item.name, ground) ? state.items.filter((it) => it !== item) : state.items;
  return { state: { ...state, ground: next, items } };
}

/**
 * Clears a tile back to how the first clearing had it: nothing set out, and its first ground,
 * growing as it grew then (Daniel, 2026-10-18).
 */
export function clearTile(state: ClearingState, x: number, y: number): Edit {
  if (!inside(state, x, y)) return { refused: "mist" };
  if (x === 0 && y === 0) return { refused: "cabin" };
  const i = cell(x, y);
  if (i === null || !clearable(state, x, y)) return { state };
  const code = CODE[startGroundAt(x, y)];
  const ground = `${state.ground.slice(0, i)}${code}${state.ground.slice(i + 1)}`;
  const item = itemAt(state, x, y);
  return { state: { ...state, ground, items: state.items.filter((it) => it !== item) } };
}

/** Whether clearing a tile would change it: something stands there, or its ground was painted. */
export function clearable(state: ClearingState, x: number, y: number): boolean {
  if (x === 0 && y === 0) return false;
  return itemAt(state, x, y) !== null || groundAt(state, x, y) !== startGroundAt(x, y);
}

/** The turn of the yaw that points a model's +x away from the land, toward open water. */
function awayFromLand(state: ClearingState, x: number, y: number): number | null {
  for (const [dx, dy] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const) {
    if (groundAt(state, x + dx, y + dy) !== "water") {
      const yaw = Math.atan2(dy, -dx);
      return (Math.round(yaw / (Math.PI / 4)) + 8) % 8;
    }
  }
  return null;
}

/**
 * How far a tap turns a model, in eighths: a sixteenth of a full turn, 22.5 degrees, for
 * everything, houses too, so the player may turn things as they like (Daniel, 2026-10-19, after
 * trying it; before, a quarter for what stands square to the grid and an eighth for the rest).
 */
const STEP = 0.5;

/** Whether a model stands square to the grid: set out square to it, as the world stands it. */
function isSquare(name: string): boolean {
  return settable(name)?.square ?? false;
}

/**
 * Sets a model out on a tile, in place of what stood there. On a tile that already holds the same
 * model it turns it instead. With `keepTurn` a new model takes the turn of the one it replaces,
 * as when another cabin is chosen for a tile being edited.
 *
 * A massif stands on 2 by 2 tiles (decision 0018), on the corner of the tile nearest `at`, the
 * point of the ground that was tapped, in tiles; without it, or when it stands where a massif
 * already is, on that massif's block, and else from the tile toward +x and +y. Whatever stood on
 * its four tiles gives way, and all four must be inside, off the cabin and on land.
 */
export function place(
  state: ClearingState,
  x: number,
  y: number,
  name: string,
  options: {
    readonly keepTurn?: boolean;
    readonly at?: { readonly x: number; readonly y: number };
  } = {},
): Edit {
  if (!inside(state, x, y)) return { refused: "mist" };
  if (x === 0 && y === 0) return { refused: "cabin" };
  const existing = itemAt(state, x, y);
  if (existing && existing.name === name) return turnAt(state, x, y);
  // Where it may stand: its own tile, or for a massif the blocks the tile belongs to, the
  // preferred one first, then the others by how near their middle is to the tapped point.
  const size = sizeOf(name);
  let anchors: Coord[] = [{ x, y }];
  if (size > 1) {
    const to = options.at ?? { x: x + 0.5, y: y + 0.5 };
    anchors = [];
    for (let dy = 0; dy < size; dy += 1) {
      for (let dx = 0; dx < size; dx += 1) anchors.push({ x: x - dx, y: y - dy });
    }
    const off = (a: Coord) => Math.hypot(a.x + (size - 1) / 2 - to.x, a.y + (size - 1) / 2 - to.y);
    anchors.sort((a, b) => off(a) - off(b));
    if (existing && sizeOf(existing.name) === size) anchors = [{ x: existing.x, y: existing.y }];
  }
  const refusal = (a: Coord): Refusal | null => {
    const tiles = tilesOf({ name, x: a.x, y: a.y });
    for (const t of tiles) {
      if (!inside(state, t.x, t.y)) return "mist";
      if (t.x === 0 && t.y === 0) return "cabin";
    }
    for (const t of tiles) {
      const ground = groundAt(state, t.x, t.y);
      if (!fits(name, ground)) return ground === "water" ? "needsLand" : "needsWater";
    }
    return null;
  };
  const anchor = anchors.find((a) => refusal(a) === null);
  if (!anchor) return { refused: refusal(anchors[0] as Coord) as Refusal };
  const { x: ax, y: ay } = anchor;
  const tiles = tilesOf({ name, x: ax, y: ay });
  const square = isSquare(name);
  let turn = square ? 0 : (mix32(ax, ay, SALT_TURN) % 16) / 2;
  if (familyOf(name) === "jetty") turn = awayFromLand(state, ax, ay) ?? 0;
  if (existing && options.keepTurn) turn = existing.turn;
  // A field stands unturned, so it joins its neighbours.
  if (familyDef(name)?.joins) turn = 0;
  const items = state.items.filter((it) => !tiles.some((t) => covers(it, t.x, t.y)));
  return { state: withItems(state, [...items, { name, x: ax, y: ay, turn }]) };
}

/**
 * Turns what stands on a tile by its step; a tile with nothing on it stays as it is, and so does a
 * field, which stands unturned so it joins its neighbours.
 */
export function turnAt(state: ClearingState, x: number, y: number): Edit {
  if (!inside(state, x, y)) return { refused: "mist" };
  if (x === 0 && y === 0) return { refused: "cabin" };
  const existing = itemAt(state, x, y);
  if (!existing || familyDef(existing.name)?.joins) return { state };
  const turned = { ...existing, turn: (existing.turn + STEP) % 8 };
  return {
    state: withItems(
      state,
      state.items.map((it) => (it === existing ? turned : it)),
    ),
  };
}

/** A smaller or larger clearing, and the tiles the mist leaves when it grows. */
export function setReach(
  state: ClearingState,
  reach: number,
): { state: ClearingState; shown: Coord[] } {
  const r = reach >= REACH_LARGE ? REACH_LARGE : REACH_SMALL;
  if (r === state.reach) return { state, shown: [] };
  const next = { ...state, reach: r };
  return { state: next, shown: newlyShown(state, next) };
}

/** The tiles inside `after` that were outside `before`. */
export function newlyShown(before: ClearingState, after: ClearingState): Coord[] {
  const out: Coord[] = [];
  for (let y = -after.reach; y <= after.reach; y += 1) {
    for (let x = -after.reach; x <= after.reach; x += 1) {
      if (!inside(before, x, y)) out.push({ x, y });
    }
  }
  return out;
}

/** The clearing as the renderer sees it: the ground inside revealed, everything beyond in mist. */
export function clearingWorld(get: () => ClearingState): WorldSource {
  return {
    isRevealed: (x, y) => inside(get(), x, y),
    hasBanner: () => false,
    adjacent: () => 0,
    terrain: (x, y) => TERRAIN[groundAt(get(), x, y)],
    isChasm: () => false,
    isClosed: () => false,
    isSettled: () => false,
    status: "playing",
    killer: null,
  };
}

/**
 * What a tile grows by itself by the world's rules, without the houses, stones and boats a run
 * would place there, since those are the player's to set out. Each tile grows on its own: a
 * massif, over four tiles, is the player's to set out too.
 */
export function naturalDress(world: WorldSource, x: number, y: number, pick: Pick): Placement[] {
  return objectsOn(world, x, y, pick, { blocks: false }).filter(
    (p) => familyDef(p.name)?.nature === true,
  );
}

/**
 * A model set out on a tile: in its middle, turned by eighths, at the water's level on water. A
 * massif stands on the corner where its four tiles meet, and rises with the last of them.
 */
export function placementOf(name: string, turn: number, water: boolean, nudge = 0): Placement {
  const family = settable(name);
  const size = sizeOf(name);
  return {
    name,
    dx: (size - 1) / 2,
    dy: (size - 1) / 2,
    yaw: (turn * Math.PI) / 4 + nudge,
    scale: 1,
    sway: family ? SWAYS[family.sway] : SWAY.none,
    h: water ? -WATER_DROP : 0,
    ...(size > 1 ? { span: size } : {}),
  };
}

const isDeer = (p: Placement) => p.name.startsWith("iso-deer");

/**
 * Where the clearing's own deer grazes. The animals stay wild and come where the ground suits
 * them (Daniel, 2026-10-19), and a deer comes to the clearing whenever it can: to a tile with
 * nothing set out where the world's rule lets a deer graze, the one whose own number is lowest,
 * so the same clearing always has it on the same tile. Null where the world's rule has already
 * put a deer in the clearing, or where no tile suits one.
 */
export function deerTile(state: ClearingState, world: WorldSource, pick: Pick): Coord | null {
  let best: Coord | null = null;
  let lowest = Number.POSITIVE_INFINITY;
  for (let y = -state.reach; y <= state.reach; y += 1) {
    for (let x = -state.reach; x <= state.reach; x += 1) {
      if (itemAt(state, x, y)) continue;
      const natural = naturalDress(world, x, y, pick);
      if (natural.some(isDeer)) return null;
      if (!canGraze(world, x, y, natural)) continue;
      const n = mix32(x, y, SALT_GRAZE);
      if (n < lowest) {
        best = { x, y };
        lowest = n;
      }
    }
  }
  return best;
}

/**
 * How each tile is dressed: what the player set out there with a little grass under it, but for a
 * field, which fills its tile alone as on an expedition, or else what it grows by itself, and the
 * clearing's deer where it grazes.
 */
export function clearingDress(
  get: () => ClearingState,
  world: WorldSource,
  pick: Pick,
): (x: number, y: number) => Placement[] | null {
  // The deer's tile, for the state it was worked out from; every change makes a new state.
  let deerFor: ClearingState | null = null;
  let deer: Coord | null = null;
  return (x, y) => {
    const state = get();
    if (!inside(state, x, y)) return null;
    if (state !== deerFor) {
      deerFor = state;
      deer = deerTile(state, world, pick);
    }
    const natural = naturalDress(world, x, y, pick);
    const item = itemAt(state, x, y);
    if (!item) return deer?.x === x && deer.y === y ? [...natural, deerAt(x, y)] : natural;
    const water = groundAt(state, x, y) === "water";
    // Nothing grows under a field, as nothing does on an expedition (Daniel, 2026-10-25).
    const under =
      water || familyDef(item.name)?.joins
        ? []
        : natural.filter((p) => p.name.startsWith("iso-tuft") || p.name.startsWith("iso-pebbles"));
    // A massif is drawn from its own tile, and only while all its tiles are inside the clearing.
    if (item.x !== x || item.y !== y) return under;
    if (!tilesOf(item).every((t) => inside(state, t.x, t.y))) return under;
    return [placementOf(item.name, item.turn, water, nudgeAt(x, y)), ...under];
  };
}

/** What a run found: every model that stood on a revealed tile, and the creature that ended it. */
export function findsIn(game: Game, pick: Pick): Set<string> {
  const out = new Set<string>();
  for (const { x, y } of game.revealedTiles()) {
    if (!bearsObjects(game, x, y)) continue;
    for (const p of objectsOn(game, x, y, pick)) out.add(p.name);
  }
  const k = game.killer;
  if (game.status === "dead" && k) out.add(creatureOn(game.terrain(k.x, k.y), k.x, k.y));
  return out;
}

const FOUND_KEY = "clearing.found";
const STATE_KEY = "clearing";

export function loadFound(): Set<string> {
  const saved = getJson<{ v: number; names: unknown }>(FOUND_KEY);
  const names = saved?.v === 1 && Array.isArray(saved.names) ? saved.names : [];
  return new Set([HOME, ...names.filter((n): n is string => typeof n === "string")]);
}

function saveFound(found: ReadonlySet<string>): void {
  setJson(FOUND_KEY, { v: 1, names: [...found].sort() });
}

/** Adds what the runs found to what was found before, keeps it, and returns it all. */
export function collectFinds(records: readonly RunRecord[], pick: Pick): Set<string> {
  const found = loadFound();
  const before = found.size;
  for (const record of records) {
    try {
      for (const name of findsIn(Game.fromRecord(record), pick)) found.add(name);
    } catch {
      // A record that no longer replays finds nothing.
    }
  }
  if (found.size !== before) saveFound(found);
  return found;
}

/** Keeps what a run that has just ended found, for the clearing. */
export function rememberFinds(game: Game, pick: Pick): void {
  const found = loadFound();
  const before = found.size;
  for (const name of findsIn(game, pick)) found.add(name);
  if (found.size !== before) saveFound(found);
}

/**
 * The tiles an action may have changed what stands on: those within two tiles of every tile it
 * revealed and of the tile it acted on, each once, in that order. A number closed by a banner, or
 * by the last open tile round it being revealed, comes to bear objects, and a block of four whose
 * last tile came to bear them shows its massif from its corner; neither is a tile the action
 * revealed, but both lie within two tiles of one (Daniel, 2026-10-21: those finds get their card
 * too). The app's ClearingController looks at the same tiles in the same order.
 */
export function touchedTiles(tiles: readonly Coord[]): Coord[] {
  const seen = new Set<string>();
  const out: Coord[] = [];
  for (const t of tiles) {
    for (let dy = -2; dy <= 2; dy += 1) {
      for (let dx = -2; dx <= 2; dx += 1) {
        const key = `${t.x + dx},${t.y + dy}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ x: t.x + dx, y: t.y + dy });
      }
    }
  }
  return out;
}

/**
 * What an action's tiles hold that can be set out and was not found before (`touchedTiles`), in
 * the order the tiles came, and the creature that ended the run once it has: found the moment it
 * shows, for the clearing in the game. Kept at once.
 */
export function noteFinds(game: Game, tiles: readonly Coord[], pick: Pick): string[] {
  const found = loadFound();
  const before = found.size;
  const fresh: string[] = [];
  const add = (name: string) => {
    if (found.has(name)) return;
    found.add(name);
    if (familyOf(name) !== null) fresh.push(name);
  };
  for (const { x, y } of touchedTiles(tiles)) {
    if (!game.isRevealed(x, y) || !bearsObjects(game, x, y)) continue;
    for (const p of objectsOn(game, x, y, pick)) add(p.name);
  }
  const k = game.killer;
  if (game.status === "dead" && k) add(creatureOn(game.terrain(k.x, k.y), k.x, k.y));
  if (found.size !== before) saveFound(found);
  return fresh;
}

/**
 * Whether an action's tiles (`touchedTiles`) show a stave church, for its bell the first time a
 * run comes upon one.
 */
export function showsChurch(game: Game, tiles: readonly Coord[], pick: Pick): boolean {
  for (const { x, y } of touchedTiles(tiles)) {
    if (!game.isRevealed(x, y) || !bearsObjects(game, x, y)) continue;
    if (objectsOn(game, x, y, pick).some((p) => familyOf(p.name) === "church")) return true;
  }
  return false;
}

/** How many of the things that can be set out have been found, of how many there are. */
export function tally(
  names: Iterable<string>,
  found: ReadonlySet<string>,
): { found: number; total: number } {
  const entries = catalog(names, found, false);
  return { found: entries.filter((e) => e.found).length, total: entries.length };
}

/** A saved clearing, checked: unknown models dropped, coordinates kept to the large clearing. */
export function parseClearing(raw: unknown, names: ReadonlySet<string>): ClearingState | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as { v?: unknown; reach?: unknown; ground?: unknown; items?: unknown };
  if (r.v !== 1 || typeof r.ground !== "string" || !Array.isArray(r.items)) return null;
  if (r.ground.length !== SIDE * SIDE || !/^[gfwm]+$/.test(r.ground)) return null;
  const reach = r.reach === REACH_LARGE ? REACH_LARGE : REACH_SMALL;
  const byTile = new Map<string, Item>();
  for (const it of r.items as unknown[]) {
    if (typeof it !== "object" || it === null) continue;
    const { name, x, y, turn } = it as { name?: unknown; x?: unknown; y?: unknown; turn?: unknown };
    if (typeof name !== "string" || !names.has(name)) continue;
    if (name !== HOME && familyOf(name) === null) continue;
    if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isFinite(turn)) continue;
    const xi = x as number;
    const yi = y as number;
    if (cell(xi, yi) === null || (xi === 0 && yi === 0)) continue;
    // A turn is in eighths, in halves at the finest.
    const t = Math.round((turn as number) * 2) / 2;
    byTile.set(`${xi},${yi}`, { name, x: xi, y: yi, turn: ((t % 8) + 8) % 8 });
  }
  // A massif's four tiles must all lie in the large clearing, off the cabin, and nothing may
  // stand on a tile something else already stands on: the first saved keeps it.
  const taken = new Set(["0,0"]);
  const items: Item[] = [];
  for (const item of byTile.values()) {
    const tiles = tilesOf(item);
    if (tiles.some((t) => cell(t.x, t.y) === null || taken.has(`${t.x},${t.y}`))) continue;
    for (const t of tiles) taken.add(`${t.x},${t.y}`);
    items.push(item);
  }
  return { v: 1, reach, ground: r.ground, items: [cabin(), ...items] };
}

export function loadClearing(names: ReadonlySet<string>): ClearingState {
  return parseClearing(getJson<unknown>(STATE_KEY), names) ?? defaultClearing();
}

export function saveClearing(state: ClearingState): void {
  setJson(STATE_KEY, state);
}

export interface CatalogEntry {
  readonly name: string;
  readonly family: string;
  readonly category: Category;
  /** Its number among the models of its kind, 1 and up, when there are several; else 0. */
  readonly variant: number;
  readonly found: boolean;
}

/** Everything that can be set out, by category and kind in the tray's order, and whether it is found. */
export function catalog(
  names: Iterable<string>,
  found: ReadonlySet<string>,
  all: boolean,
): CatalogEntry[] {
  const order = CATALOG.families.filter((f) => f.category !== null);
  const byFamily = new Map<string, string[]>();
  for (const name of names) {
    if (name === HOME) continue;
    const family = familyOf(name);
    if (family === null) continue;
    const list = byFamily.get(family) ?? [];
    list.push(name);
    byFamily.set(family, list);
  }
  const out: CatalogEntry[] = [];
  for (const def of order) {
    const list = (byFamily.get(def.name) ?? []).sort((a, b) =>
      a.localeCompare(b, "en", { numeric: true }),
    );
    const category = def.category as Category;
    list.forEach((name, i) => {
      out.push({
        name,
        family: def.name,
        category,
        variant: list.length > 1 ? i + 1 : 0,
        found: all || found.has(name),
      });
    });
  }
  return out;
}
